import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { getFavoriteProperties, removeFavorite, Favorite } from "@/api/favorites";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Heart,
  SlidersHorizontal,
  Bed,
  Bath,
  Square,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { PropertyCard } from "@/components/PropertyCard";
import { Property } from "@/types/property";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FavoriteButton } from "@/components/FavoriteButton";

export function FavoritesPage() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<string>("date-desc");
  const [viewMode, setViewMode] = useState<string>("grid");

  const fetchFavorites = async () => {
    try {
      setIsLoading(true);
      const fetchedFavorites = await getFavoriteProperties();
      console.log('Fetched favorites:', fetchedFavorites);
      setFavorites(fetchedFavorites);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Error al cargar los favoritos', {
        description: 'Hubo un problema al obtener tus propiedades favoritas. Intenta de nuevo más tarde.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user]);

  const handleRemoveFavorite = async (propertyId: string) => {
    try {
      const result = await removeFavorite(propertyId);
      
      if (result.success) {
        // Actualizar la lista de favoritos localmente
        setFavorites(favorites.filter(fav => {
          const favPropertyId = typeof fav.propertyId === 'object' ? fav.propertyId._id : fav.propertyId;
          const propertyToCompare = fav.property?._id || favPropertyId;
          return propertyToCompare !== propertyId;
        }));
        
        // Solo mostrar notificación si no hay un mensaje específico (como "ya no está en favoritos")
        if (!result.message) {
          toast.success('Propiedad eliminada de favoritos');
        }
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
      // No mostrar toast de error, ya que el componente FavoriteButton se encarga de eso
    }
  };

  // Función para obtener la propiedad de un favorito
  const getProperty = (favorite: Favorite): Property | null => {
    if (favorite.property) {
      return favorite.property;
    }
    
    if (typeof favorite.propertyId === 'object') {
      return favorite.propertyId as Property;
    }
    
    return null;
  };

  const sortedFavorites = [...favorites].sort((a, b) => {
    const propertyA = getProperty(a);
    const propertyB = getProperty(b);
    
    if (!propertyA || !propertyB) return 0;
    
    switch (sortBy) {
      case "price-asc":
        return (propertyA.price || 0) - (propertyB.price || 0);
      case "price-desc":
        return (propertyB.price || 0) - (propertyA.price || 0);
      case "date-asc":
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case "date-desc":
      default:
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Favoritos</h2>
            <p className="text-muted-foreground">
              Gestiona tus propiedades favoritas
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchFavorites}
            disabled={isLoading}
          >
            {isLoading ? 'Actualizando...' : 'Actualizar'}
          </Button>
        </div>

        {/* Stats Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total de Favoritos
            </CardTitle>
            <Heart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{favorites.length}</div>
          </CardContent>
        </Card>

        {/* Favorites Display */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle>Propiedades Favoritas</CardTitle>
                <CardDescription>
                  Lista de todas las propiedades que has marcado como favoritas
                </CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Tabs defaultValue="grid" value={viewMode} onValueChange={setViewMode} className="w-[200px]">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="grid">Cuadrícula</TabsTrigger>
                    <TabsTrigger value="list">Lista</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Ordenar por" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date-desc">Más recientes</SelectItem>
                      <SelectItem value="date-asc">Más antiguos</SelectItem>
                      <SelectItem value="price-desc">Mayor precio</SelectItem>
                      <SelectItem value="price-asc">Menor precio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : favorites.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-lg font-medium mb-2">No tienes propiedades favoritas</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Explora nuestras propiedades y marca tus favoritas para verlas aquí
                </p>
                <Button asChild>
                  <a href="/">Explorar propiedades</a>
                </Button>
              </div>
            ) : (
              <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-4"}>
                {sortedFavorites.map((favorite) => {
                  const property = getProperty(favorite);
                  
                  if (!property) {
                    console.log('Favorite without valid property:', favorite);
                    return null;
                  }
                  
                  const propertyId = property._id || '';
                  
                  return (
                    <div key={favorite._id} className={viewMode === "list" ? "relative border rounded-lg p-4" : ""}>
                      {viewMode === "grid" ? (
                        <div className="relative">
                          <PropertyCard 
                            _id={propertyId}
                            title={property.title}
                            description={property.description}
                            price={property.price}
                            location={property.location}
                            images={property.images}
                            type={property.type}
                            propertyType={property.propertyType}
                            bedrooms={property.bedrooms}
                            bathrooms={property.bathrooms}
                            area={property.area}
                            features={property.features}
                          />
                          <div className="absolute top-2 right-2 z-10">
                            <FavoriteButton 
                              propertyId={propertyId}
                              initialIsFavorite={true}
                              onFavoriteChange={(isFavorite: boolean) => {
                                if (!isFavorite) {
                                  // Actualizar la lista de favoritos localmente sin llamar a la API de nuevo
                                  setFavorites(favorites.filter(fav => {
                                    const favPropertyId = typeof fav.propertyId === 'object' ? fav.propertyId._id : fav.propertyId;
                                    const propertyToCompare = fav.property?._id || favPropertyId;
                                    return propertyToCompare !== propertyId;
                                  }));
                                }
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col md:flex-row gap-4">
                          <div className="w-full md:w-1/3 h-[200px] relative rounded-md overflow-hidden">
                            {property.images && property.images.length > 0 ? (
                              <img 
                                src={property.images[0]} 
                                alt={property.title} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-muted flex items-center justify-center">
                                <p className="text-muted-foreground">Sin imagen</p>
                              </div>
                            )}
                            <div className="absolute top-2 right-2">
                              <FavoriteButton 
                                propertyId={propertyId}
                                initialIsFavorite={true}
                                onFavoriteChange={(isFavorite: boolean) => {
                                  if (!isFavorite) {
                                    // Actualizar la lista de favoritos localmente sin llamar a la API de nuevo
                                    setFavorites(favorites.filter(fav => {
                                      const favPropertyId = typeof fav.propertyId === 'object' ? fav.propertyId._id : fav.propertyId;
                                      const propertyToCompare = fav.property?._id || favPropertyId;
                                      return propertyToCompare !== propertyId;
                                    }));
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold mb-2">{property.title}</h3>
                            <p className="text-muted-foreground mb-2">
                              {typeof property.location === 'object' 
                                ? property.location.address 
                                : (typeof property.location === 'string' 
                                    ? property.location 
                                    : 'Ubicación no disponible')}
                            </p>
                            <p className="text-lg font-bold mb-4">${property.price?.toLocaleString()}</p>
                            <div className="flex gap-4 mb-4">
                              <div className="flex items-center gap-1">
                                <Bed className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{property.bedrooms || 0}</span>
                                <span className="text-xs text-muted-foreground">Hab</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Bath className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{property.bathrooms || 0}</span>
                                <span className="text-xs text-muted-foreground">Baños</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Square className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{property.area || 0}</span>
                                <span className="text-xs text-muted-foreground">m²</span>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-4">
                              Guardado el {format(new Date(favorite.createdAt), "d 'de' MMMM, yyyy", { locale: es })}
                            </p>
                            <div className="flex gap-2">
                              <Button asChild variant="outline" size="sm">
                                <a href={`/property/${propertyId}`}>Ver detalles</a>
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => handleRemoveFavorite(propertyId)}
                              >
                                <Heart className="h-4 w-4 mr-2" />
                                Eliminar
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
} 